import { gettext } from '@eventbrite/i18n';
import classnames from 'classnames';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import './CarouselSwiper.scss';
import {
    getNextItem,
    getPrevItem,
    getSlidesTranslationValue,
    getSlideTranslateX,
    isLastItem,
} from './helpers';
import { useKeyPress } from './useKeyPress';
import { useSwipe } from './useSwipe';

export type CarouselSwiperControllerProps = {
    initialSlide: number;
    items: ReactNode[];
};

export const useCarouselSwiperController = ({
    initialSlide = 0,
    items,
}: CarouselSwiperControllerProps) => {
    const [currentIndex, setCurrentIndex] = useState(initialSlide);

    return {
        currentIndex,
        setCurrentIndex,
        isLastSlide: isLastItem(currentIndex, items),
        isFirstSlide: currentIndex === 0,
        prevSlide: getPrevItem(currentIndex, items),
        nextSlide: getNextItem(currentIndex, items),
    };
};
export interface CarouselSwiperProps {
    className?: string;
    currentIndex: number;
    enableNavigationLooping?: boolean;
    gap?: string;
    items: ReactNode[];
    setCurrentIndex: (index: number) => void;
    controls?: ReactNode;
    progressClassName?: string;
    viewportClassName?: string;
    useBottomProgressBar?: boolean;
    // hide progress bars
    hideProgressBars?: boolean;
    // adds an overlay to disable clicks on all slides except the current, active slide
    disableNextSlides?: boolean;
    // animate slides on transition
    animateSlides?: boolean;
    // override position of the last slide
    snapToEndPosition?: string;
    // hide the previous slide so that it doesn't show any edges
    hidePreviousSlide?: boolean;
    // override position of each slide. Helpful if slide width changes
    customTranslate?: string;
}

export const CarouselSwiper: React.FC<CarouselSwiperProps> = ({
    className,
    currentIndex,
    enableNavigationLooping = false,
    gap = '',
    items,
    controls,
    progressClassName,
    setCurrentIndex,
    viewportClassName,
    useBottomProgressBar,
    hideProgressBars,
    disableNextSlides,
    animateSlides,
    snapToEndPosition,
    hidePreviousSlide,
    customTranslate,
}) => {
    const styles = { '--gap': gap } as React.CSSProperties;
    const firstSlide = 0;
    const lastSlide = items.length - 1;

    const [translationUnit, setTranslationUnit] = useState<string>(
        getSlidesTranslationValue(currentIndex),
    );
    const [isCarouselFocused, setIsCarouselFocused] = useState(false);
    const shouldHideLeftControl =
        currentIndex === firstSlide && !enableNavigationLooping;
    const shouldHideRightControl =
        currentIndex === lastSlide && !enableNavigationLooping;
    const carousel = useRef(null);

    useEffect(() => {
        setTranslationUnit(getSlidesTranslationValue(currentIndex));
    }, [currentIndex]);

    const nextSlide = () => {
        const nextSlide = getNextItem(currentIndex, items);
        setCurrentIndex(nextSlide);
    };

    const prevSlide = () => {
        const prevSlide = getPrevItem(currentIndex, items);
        setCurrentIndex(prevSlide);
    };

    const handleOnFocus = () => {
        setIsCarouselFocused(true);
    };

    const handleOnBlur = (event: React.FocusEvent<HTMLDivElement>) => {
        const isCarouselBlurred = !event.currentTarget.contains(
            event.relatedTarget as Node,
        );
        if (isCarouselBlurred) {
            setIsCarouselFocused(false);
        }
    };

    const shouldDisableLeftNavigation =
        !enableNavigationLooping && shouldHideLeftControl;
    const shouldDisableRightNavigation =
        !enableNavigationLooping && shouldHideRightControl;

    useKeyPress({
        isCarouselFocused,
        onLeftKeyPressed: () => prevSlide(),
        onRightKeyPressed: () => nextSlide(),
        shouldDisableLeftNavigation,
        shouldDisableRightNavigation,
    });

    useSwipe({
        ref: carousel,
        onSwipeLeft: () => nextSlide(),
        onSwipeRight: () => prevSlide(),
        shouldDisableLeftNavigation,
        shouldDisableRightNavigation,
    });

    const progressItems = items.map((_, index: number) => {
        const isCurrentSlide = index === currentIndex;
        return (
            <li key={index} data-testid="eds-carousel-swiper-progress-item">
                <button
                    className={classnames(
                        'eds-carousel-swiper-progress-list-item-button',
                        {
                            'eds-carousel-swiper-progress-list-item-button-selected':
                                isCurrentSlide,
                        },
                    )}
                    onClick={() => setCurrentIndex(index)}
                    disabled={isCurrentSlide}
                    aria-current={isCurrentSlide}
                    aria-label={gettext('Go to slide %(slideNumber)s', {
                        slideNumber: index + 1,
                    }).toString()}
                    type="button"
                />
            </li>
        );
    });

    const viewportItems = items.map((item, index: number) => (
        <li
            className="eds-carousel-swiper-viewport-list-item"
            data-testid="slide"
            aria-hidden={index !== firstSlide}
            key={`slide-${index}`}
            onClick={() =>
                [currentIndex - 1, currentIndex + 1].includes(index)
                    ? setCurrentIndex(index)
                    : null
            }
            style={{
                opacity:
                    hidePreviousSlide &&
                    index < currentIndex &&
                    index !== items.length - 2 // show second to last slide
                        ? 0
                        : 1,
                transition: `all 0.2s ease-in-out`,
            }}
        >
            {disableNextSlides && index !== currentIndex ? (
                <div
                    className="eds-carousel-swiper-viewport-list-item-overlay"
                    data-testid="list-item-overlay"
                ></div>
            ) : null}
            <div>{item}</div>
        </li>
    ));

    return (
        <div
            ref={carousel}
            tabIndex={0}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            data-testid={'carousel-swiper'}
            className={classnames('eds-carousel-swiper-wrapper', className)}
            style={styles}
        >
            <div>{controls}</div>
            <div
                className={classnames(
                    'eds-carousel-swiper-progress',
                    progressClassName,
                )}
            >
                {!useBottomProgressBar && !hideProgressBars ? (
                    <ul className="eds-carousel-swiper-progress-list">
                        {progressItems}
                    </ul>
                ) : null}
            </div>

            <div className="eds-carousel-swiper-viewport">
                <ul
                    className={classnames(
                        'eds-carousel-swiper-viewport-list',
                        viewportClassName,
                    )}
                    style={{
                        transform: getSlideTranslateX({
                            gap,
                            slidePosition: currentIndex,
                            translationValue: translationUnit,
                            snapToEndPosition:
                                currentIndex === lastSlide
                                    ? snapToEndPosition
                                    : undefined,
                            customTranslate: customTranslate,
                        }),
                        transition: animateSlides ? `transform .2s` : undefined,
                    }}
                    data-testid="slide-list"
                >
                    {viewportItems}
                </ul>
            </div>
            {useBottomProgressBar && !hideProgressBars ? (
                <ul
                    className="eds-carousel-swiper-progress-list"
                    data-testid="progress-list-bottom"
                >
                    {progressItems}
                </ul>
            ) : null}
        </div>
    );
};
